import 'add-to-calendar-button/assets/css/atcb.css';
import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Link } from 'react-router-dom';
import { Card, Grid } from 'semantic-ui-react';

import { EventsProps } from '../../reducers/DashboardReducer/EventsReducer/EventsReducer.types';
import { BaseFiltersOptions } from '../../reducers/Events/FiltersReducer/FiltersReducer.types';
import { addModifiers } from '../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { logErrorService } from '../../shared/services';
import { ImmutableInterface } from '../../types';
import { Loader } from '../Loader';

import Context from './Context';

interface EventProps {
  eventsOpen: Immutable.List<string>;
  handleToogleEventsCards: (event: string) => any;
  event: ImmutableInterface<EventsProps>;
  languageOptions: Immutable.List<BaseFiltersOptions>;
  isHighlighted: boolean;
  onHighlightFinish: () => void;
}

interface EventHeaderProps {
  type: string;
  event: ImmutableInterface<EventsProps>;
}

interface EventsDateOrDescriptionProps {
  withDesc?: boolean;
  event: ImmutableInterface<EventsProps>;
}

interface EventContentProps {
  handleSetStatus: (eventID: string) => void;
  event: ImmutableInterface<EventsProps>;
  languageOptions: Immutable.List<BaseFiltersOptions>;
}

interface EventToCalendarProps {
  event: ImmutableInterface<EventsProps>;
}

const getDuration = (event: ImmutableInterface<EventsProps>, intl: any) => {
  const diffInMinutes = (event.get('end').getTime() - event.get('start').getTime()) / (1000 * 60);
  if (diffInMinutes < 60) {
    return formatMessageHelper(intl, 'event.minutes', { minutes: diffInMinutes });
  } else {
    const hours = Math.floor(diffInMinutes / 60.0);
    const minutes = diffInMinutes % 60;
    const hoursDisplay = formatMessageHelper(intl, 'event.hours', { hours });
    const minutesDisplay = minutes > 0 ? formatMessageHelper(intl, 'event.minutes', { minutes: Math.round(minutes) }) : '';

    return `${hoursDisplay}${minutesDisplay}`;
  }
};

const AddEvent = ({ event }: EventToCalendarProps & InjectedIntlProps) => {
  const description = event.get('description');
  const location = event.get('venue');
  const buttonRef = useRef<HTMLAnchorElement>(null);

  const handleCalendarButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!buttonRef.current) {
      return;
    }

    import(/* webpackPreload: true */ 'add-to-calendar-button')
      .then(({ atcb_action }) => {
        atcb_action(
          {
            name: event.get('title'),
            description,
            location,
            startDate: moment(event.get('start')).format('YYYY-MM-DD'),
            endDate: moment(event.get('end')).format('YYYY-MM-DD'),
            startTime: moment(event.get('start')).format('HH:mm'),
            endTime: moment(event.get('end')).format('HH:mm'),
            // @ts-expect-error react-add-to-calendar options not in its @types
            options: ['Apple', 'Google', 'iCal| Calendar File', 'Microsoft365', 'Outlook.com', 'Yahoo'],
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            iCalFileName: 'Reminder-Event',
            //listStyle: 'modal',
            trigger: 'click',
            background: false,
          },
          buttonRef.current
        );
      })
      .catch(error => logErrorService(error));
  };

  return (
    <a id={`calendar-trigger${event.get('id')}`} ref={buttonRef} onClick={handleCalendarButton} className="link">
      + Add to calendar
    </a>
  );
};

const EventToCalendar = injectIntl(AddEvent);

const EventsDateOrDesc = ({ event, withDesc = false }: EventsDateOrDescriptionProps) => {
  if (withDesc && event.get('description')) {
    return (
      <React.Fragment>
        <h4 className="events-card__sub-title">
          <FormattedMessageHelper id="events.description" />
        </h4>
        <div className="events-card__desc">{event.get('description')}</div>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <h4 className="events-card__sub-title">
          <FormattedMessageHelper id="event.date.time" />
        </h4>

        <div className="events-card__block">
          <div className="events-card__date">
            {moment(event.get('start')).format('MMMM Do, YYYY hh:mmA')}&nbsp;
            <FormattedMessageHelper id="event.you.time" />
          </div>
          <div className="events-card__date">
            ({moment(event.get('start')).tz(event.get('timezone')).format('MMMM Do, YYYY hh:mmA')}
            &nbsp;
            <FormattedMessageHelper id="event.local.time" />)
          </div>
        </div>
      </React.Fragment>
    );
  }
};

const EventContent = injectIntl(({ event, handleSetStatus, intl, languageOptions }: EventContentProps & InjectedIntlProps) => {
  const handle = () => handleSetStatus(event.get('id'));

  const isWithRegistration = event.get('register') && event.get('register') !== '';

  return (
    <div className="events-card__container">
      <div className="events-card__container-accordion-btn">
        <button className="button accordion-btn" onClick={handle} />
      </div>
      <EventsDateOrDesc event={event} withDesc={true} />
      <div className="events-card__content">
        {event.get('description') && <EventsDateOrDesc event={event} />}
        <div className="events-card__block">
          <h4 className="events-card__sub-title">
            <FormattedMessageHelper id="event.duration" />
          </h4>
          {event.get('start') && event.get('end') && getDuration(event, intl)}
        </div>

        <div className="events-card__block">
          <>
            <h4 className="events-card__sub-title">
              <FormattedMessageHelper id="event.language" />
            </h4>
            {languageOptions && languageOptions.filter((key: any) => key.get('value') === event.get('language')).getIn([0, 'text'], '')}
          </>
        </div>

        {event.get('venue') && (
          <div className="events-card__block">
            <h4 className="events-card__sub-title">
              <FormattedMessageHelper id="event.venue.location" />
            </h4>
            {event.get('venue')}
          </div>
        )}

        <div className="events-card__footer">
          <div className="events-card__calendar">
            <EventToCalendar event={event} />
          </div>

          {(isWithRegistration || event.get('venue')) && (
            <div className="events-card__footer-btns">
              {isWithRegistration && (
                <a className="button button--primary button--center" href={event.get('register')} target="_blank" rel="noreferrer">
                  <FormattedMessageHelper id="events.registerHere" />
                </a>
              )}
              {event.get('venue') && (
                <Link
                  className="button button--primary button--center"
                  to={
                    Immutable.fromJS(['CN', 'HK']).includes(event.get('country'))
                      ? `//api.map.baidu.com/geocoder?address=${event.get('venue')}&output=html`
                      : `//www.google.com/maps?q=${event.get('venue')}`
                  }
                  target="_blank"
                >
                  <FormattedMessageHelper id="events.get.directions" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const EventHeader = ({ event, type }: EventHeaderProps) => (
  <div className="events-card__header">
    <div className="events-card__elem-2">
      <div className={`events-card__type events-card__type--${type}`}>
        {type === 'venue' ? <FormattedMessageHelper id="event.venue" /> : <FormattedMessageHelper id="event.online" />}
      </div>
      <Link to={`/events/${event.get('id')}`} className="events-card__link">
        <div className="events-card__link-container">
          <div className="events-card__time"> {moment(event.get('start')).format('ddd. MMMM D')}</div>
          <i className="events-card__link-icon linkify icon" />
        </div>
      </Link>
    </div>
    <h3 className="events-card__header-title">{event.get('title')}</h3>
  </div>
);

export const EventCard = ({
  event,
  languageOptions,
  eventsOpen,
  handleToogleEventsCards,
  isHighlighted,
  onHighlightFinish,
}: EventProps) => {
  let cardClass = `ws-card ws-card__type ws-card__type--${event.get('type')}`;
  cardClass = eventsOpen.includes(event.get('id')) ? cardClass.concat(' selected') : cardClass;
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHighlighted && cardRef && cardRef.current) {
      cardRef.current.scrollIntoView();
      setTimeout(onHighlightFinish, 1500); //remove animation from next renders
    }
  }, [cardRef, isHighlighted]);

  return (
    <div ref={cardRef}>
      <Card className={cardClass}>
        <Card.Content className={addModifiers('events-card', isHighlighted ? ['highlighted'] : [])}>
          <EventHeader event={event} type={event.get('type') === 'OFFLINE' ? 'venue' : 'online'} />
          <EventContent event={event} handleSetStatus={handleToogleEventsCards} languageOptions={languageOptions} />
        </Card.Content>
      </Card>
    </div>
  );
};

export const List = () => (
  <Context.Consumer>
    {({ data, isFetching, isFetchingOptions, languageOptions, eventsOpen, handleToogleEventsCards, highlightedId, onHighlightFinish }) => {
      if (isFetching || isFetchingOptions) {
        return <Loader />;
      } else {
        return (
          <div className="events-wrapper-grid">
            <Grid columns={3} className="grid__col-3">
              {data.map((event: ImmutableInterface<EventsProps>) => (
                <Grid.Column key={event.get('id')} tablet={8} mobile={16} computer={5}>
                  <EventCard
                    event={event}
                    languageOptions={languageOptions}
                    eventsOpen={eventsOpen}
                    handleToogleEventsCards={handleToogleEventsCards}
                    isHighlighted={highlightedId === event.get('id')}
                    onHighlightFinish={onHighlightFinish}
                  />
                </Grid.Column>
              ))}
            </Grid>
          </div>
        );
      }
    }}
  </Context.Consumer>
);
